<template>
  <div class="inline-form">
    <div class="inline-form-title">Cadastrar serviço</div>
    <div class="columns">
      <div
        class="column col-8 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.name.$error }"
      >
        <label class="form-label">Nome do serviço</label>
        <input
          ref="name"
          type="text"
          class="form-input"
          v-model="form.name"
          placeholder="Nome do serviço"
          autocomplete="nope"
          @blur="$v.form.name.$touch()"
        >
        <div class="form-input-hint" v-if="$v.form.name.$error && !$v.form.name.required">
          Campo obrigatório
        </div>
      </div>
      <div
        class="column col-4 col-sm-12 form-group"
        :class="{ 'has-error': $v.form.type.$error }"
      >
        <label class="form-label">Tipo</label>
        <select class="form-select" v-model="form.type" @change="$v.form.type.$touch()">
          <option value="">[Selecione]</option>
          <option v-for="(label, key) in expenseTypes" :value="key" :key="key">{{ label }}</option>
        </select>
        <div class="form-input-hint" v-if="$v.form.type.$error && !$v.form.type.required">
          Campo obrigatório
        </div>
      </div>
      <div
        class="column col-12 form-group"
        :class="{ 'has-error': $v.form.specialtyCode.$error }"
        v-if="hasSpecialty"
      >
        <label class="form-label">Especialidade</label>
        <select
          class="form-select"
          v-model="form.specialtyCode"
          :disabled="loadingSpecialties"
          @change="$v.form.specialtyCode.$touch()"
        >
          <option value="">{{ loadingSpecialties ? 'Carregando...' : '[Selecione]' }}</option>
          <option v-for="item in specialties" :value="item.code" :key="item.code">
            {{ item.name }}
          </option>
        </select>
        <div
          class="form-input-hint"
          v-if="$v.form.specialtyCode.$error && !$v.form.specialtyCode.required"
        >Campo obrigatório</div>
      </div>
    </div>

    <div class="inline-note">
      <fa-icon :icon="['fal', 'info-circle']"/>
      <span>
        O serviço não entra em nenhuma tabela de preços. Inclua nas tabelas dos convênios
        que atendem: só assim ele aparece no agendamento desses convênios.
      </span>
    </div>

    <div class="inline-form-actions">
      <button class="btn btn-gray" :disabled="saving" @click="$emit('cancel')">Fechar</button>
      <button
        class="btn btn-secondary btn-icon btn-icon-left"
        :class="{ loading: saving }"
        :disabled="saving"
        @click="save"
      ><fa-icon :icon="['fal', 'save']"/>Adicionar</button>
    </div>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { types as expenseTypes } from '@/data/expense-types';

const SPECIALTY_TYPES = ['consultation', 'session', 'evaluation'];

export default {
  props: {
    initialName: {
      type: String,
      default: '',
    },
    preferredSpecialties: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      specialties: [],
      loadingSpecialties: false,
      saving: false,
      expenseTypes,
      form: {
        name: '',
        type: '',
        specialtyCode: '',
      },
    };
  },
  computed: {
    hasSpecialty() {
      return SPECIALTY_TYPES.includes(this.form.type);
    },
  },
  watch: {
    hasSpecialty(value) {
      if (!value) {
        this.form.specialtyCode = '';
      } else if (this.specialties.length === 0) {
        this.loadSpecialties();
      } else if (!this.form.specialtyCode && this.preferredSpecialties.length === 1) {
        [this.form.specialtyCode] = this.preferredSpecialties;
      }
    },
  },
  validations() {
    const rules = {
      form: {
        name: { required },
        type: { required },
        specialtyCode: {},
      },
    };

    if (this.hasSpecialty) {
      rules.form.specialtyCode = { required };
    }

    return rules;
  },
  mounted() {
    this.form.name = this.initialName;
    this.$nextTick(() => {
      if (this.$refs.name) this.$refs.name.focus();
    });
  },
  methods: {
    loadSpecialties() {
      this.loadingSpecialties = true;
      return this.$httpX
        .get('/services/cbos', { params: { limit: 0, isSpecialty: true } })
        .then(({ data }) => {
          const preferred = this.preferredSpecialties;
          this.specialties = [...data.items].sort((a, b) => {
            const pa = preferred.includes(a.code) ? 0 : 1;
            const pb = preferred.includes(b.code) ? 0 : 1;
            return pa - pb || a.name.localeCompare(b.name);
          });
          if (!this.form.specialtyCode && preferred.length === 1) {
            [this.form.specialtyCode] = preferred;
          }
        })
        .catch(() => {
          this.$toast.show('Ocorreu um erro ao carregar as especialidades!', { type: 'error' });
        })
        .finally(() => {
          this.loadingSpecialties = false;
        });
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      this.saving = true;

      const payload = {
        name: this.form.name,
        type: this.form.type,
        active: true,
      };

      if (this.hasSpecialty) {
        payload.specialtyCode = this.form.specialtyCode;
      }

      await this.$httpX
        .post('/expenses', payload)
        .then(({ data }) => {
          const specialty = this.hasSpecialty
            ? this.specialties.find(({ code }) => code === this.form.specialtyCode) || null
            : null;
          this.$toast.show('Serviço cadastrado com sucesso');
          this.$emit('saved', {
            id: data.id,
            name: data.name || this.form.name,
            type: data.type || this.form.type,
            specialty,
            isNew: true,
          });
        })
        .catch((e) => {
          const message = e.response && e.response.data && e.response.data.message
            ? e.response.data.message
            : 'Erro ao salvar serviço';
          this.$toast.show(message, { type: 'error' });
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>
